import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import CodeBlock from '../components/CodeBlock';

const grpoCode = `def grpo_loss(log_probs, ref_log_probs, rewards, beta=0.01):
    # Group-relative advantages
    advantages = (rewards - rewards.mean()) / (rewards.std() + 1e-8)
    
    # KL divergence penalty
    kl = (log_probs.exp() * (log_probs - ref_log_probs)).sum()
    
    # Combined loss
    loss = -(advantages * log_probs).mean() + beta * kl.mean()
    
    return loss`;

const GRPOSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Group Relative Policy Optimization - Simpler and more stable than PPO"
      >
        GRPO Algorithm
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Why GRPO */}
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Why GRPO over PPO?</h3>
            
            <div className="space-y-4 mb-6">
              <div className="slide-card">
                <p className="text-sm">
                  <span className="text-muted-foreground">Traditional PPO requires:</span>
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                    Separate critic network
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                    Complex advantage calculation
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                    Clipping mechanism
                  </li>
                </ul>
              </div>
              
              <div className="slide-card border-accent/30">
                <p className="text-sm">
                  <span className="text-accent">GRPO simplifies:</span>
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    No critic network needed
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    Direct batch comparison
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    More stable for LLMs
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
              <p className="text-sm font-medium text-foreground">Key Innovation</p>
              <p className="text-sm text-muted-foreground mt-1">
                Group-relative advantages: normalize rewards within batch rather than using absolute values.
              </p>
            </div>
          </div>
          
          {/* Code */}
          <div>
            <CodeBlock 
              code={grpoCode}
              title="grpo.py"
            />
            
            <div className="mt-4 slide-card">
              <h4 className="font-heading font-semibold text-foreground mb-3">GRPO Innovations (2025)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-primary">1.</span>
                  <span className="text-muted-foreground">Zero-gradient filtering - Skip vanishing gradients</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary">2.</span>
                  <span className="text-muted-foreground">Active sampling - Prioritize hard examples</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary">3.</span>
                  <span className="text-muted-foreground">Token-level rewards - Credit each reasoning step</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default GRPOSlide;
